import React, { useEffect, useRef, useState } from "react";
import {
  fromHtmlTimeValue,
  isValidHmsInput,
  normalizeHmsInput,
  parseHmsParts,
  toHtmlTimeValue,
} from "../../utils/samplingCpueUtils";

const HOUR_OPTIONS = Array.from({ length: 100 }, (_, i) => i);
const MIN_SEC_OPTIONS = Array.from({ length: 60 }, (_, i) => i);

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Digits-only typing → auto-insert ":".
 * Examples: "14" → "14", "1420" → "14:20", "142058" → "14:20:58"
 */
export function autoFormatHmsTyping(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 6);
  if (!digits) return "";

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4)}`;
}

/**
 * Text input for power-supply / elapsed timer values (HH:MM:SS).
 * Type digits (colons inserted) or open the Hrs/Min/Sec picker — no AM/PM.
 */
const ElapsedTimeInput = ({
  id,
  name,
  value,
  onChange,
  className = "",
  style,
  placeholder = "00:00:00",
  disabled,
  onBlur: onBlurProp,
  onFocus: onFocusProp,
  ...rest
}) => {
  const rootRef = useRef(null);
  const [localValue, setLocalValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickH, setPickH] = useState(0);
  const [pickM, setPickM] = useState(0);
  const [pickS, setPickS] = useState(0);

  useEffect(() => {
    if (!focused) {
      setLocalValue(value ? toHtmlTimeValue(value) : "");
    }
  }, [value, focused]);

  useEffect(() => {
    if (!pickerOpen) return;
    const parts = parseHmsParts(value || localValue) || { h: 0, m: 0, s: 0 };
    setPickH(Math.min(99, parts.h));
    setPickM(parts.m);
    setPickS(parts.s);
  }, [pickerOpen, value, localValue]);

  useEffect(() => {
    if (!pickerOpen) return;
    const close = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [pickerOpen]);

  const emitChange = (nextValue) => {
    onChange?.({
      target: {
        id: id || name,
        name: name || id,
        value: nextValue,
      },
    });
  };

  const applyPickedTime = (h, m, s) => {
    const display = `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
    const stored = fromHtmlTimeValue(display);
    emitChange(stored);
    setLocalValue(display);
  };

  const handlePickerChange = (field, raw) => {
    const n = parseInt(raw, 10);
    const h = field === "h" ? n : pickH;
    const m = field === "m" ? n : pickM;
    const s = field === "s" ? n : pickS;
    setPickH(h);
    setPickM(m);
    setPickS(s);
    applyPickedTime(h, m, s);
  };

  const handleChange = (e) => {
    const formatted = autoFormatHmsTyping(e.target.value);
    setLocalValue(formatted);

    const digits = formatted.replace(/\D/g, "");
    if (digits.length === 6 && isValidHmsInput(normalizeHmsInput(formatted))) {
      const stored = fromHtmlTimeValue(normalizeHmsInput(formatted));
      emitChange(stored);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === ":" || e.key === ";" || e.key === ".") {
      e.preventDefault();
    }
  };

  const handleFocus = (e) => {
    setFocused(true);
    onFocusProp?.(e);
  };

  const handleBlur = (e) => {
    if (rootRef.current?.contains(e.relatedTarget)) {
      return;
    }
    setFocused(false);
    let raw = e.target.value.trim();
    if (!raw) {
      emitChange("");
      setLocalValue("");
      onBlurProp?.(e);
      return;
    }

    // Pad partial digit entry to HH:MM:SS on blur
    const digits = raw.replace(/\D/g, "").padEnd(6, "0").slice(0, 6);
    raw = autoFormatHmsTyping(digits);

    const normalized = normalizeHmsInput(raw);
    if (isValidHmsInput(normalized)) {
      const stored = fromHtmlTimeValue(normalized);
      emitChange(stored);
      setLocalValue(toHtmlTimeValue(stored));
    } else {
      emitChange(raw);
      setLocalValue(autoFormatHmsTyping(raw));
    }
    onBlurProp?.(e);
  };

  const togglePicker = () => {
    if (disabled) return;
    setPickerOpen((open) => !open);
  };

  const pickerSelectStyle = {
    width: "4.25rem",
    fontSize: "0.85rem",
    padding: "0.25rem 0.35rem",
    borderRadius: "6px",
    border: "1px solid var(--wf-input-border, #dee2e6)",
    backgroundColor: "var(--wf-input-bg, #fff)",
    color: "var(--wf-input-text, inherit)",
  };

  return (
    <div
      ref={rootRef}
      className={`elapsed-time-input position-relative ${className}`.trim()}
      style={style}
    >
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        id={id}
        name={name}
        className="elapsed-time-input__field"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          outline: "none",
          background: "transparent",
          color: "inherit",
          paddingRight: "2.25rem",
          fontSize: "inherit",
        }}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        title="Type digits (colons added for you) or pick Hrs / Min / Sec"
        {...rest}
      />
      <button
        type="button"
        className="elapsed-time-input__trigger btn btn-link p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-2"
        onClick={togglePicker}
        disabled={disabled}
        tabIndex={-1}
        aria-label="Open elapsed time picker"
        aria-expanded={pickerOpen}
        style={{
          color: "var(--wf-text-muted, #6c757d)",
          lineHeight: 1,
          textDecoration: "none",
        }}
      >
        <i className="fa fa-clock-o" aria-hidden />
      </button>

      {pickerOpen && (
        <div
          className="elapsed-time-input__picker shadow-sm rounded-2 p-2 position-absolute end-0"
          style={{
            top: "calc(100% + 4px)",
            zIndex: 1050,
            backgroundColor: "var(--wf-input-bg, #fff)",
            border: "1px solid var(--wf-input-border, #dee2e6)",
            minWidth: "14.5rem",
          }}
          role="dialog"
          aria-label="Elapsed time picker"
        >
          <div className="d-flex gap-2 justify-content-center mb-1">
            <div className="text-center">
              <div className="small text-muted mb-1">Hrs</div>
              <select
                aria-label="Hours"
                value={pickH}
                onChange={(e) => handlePickerChange("h", e.target.value)}
                style={pickerSelectStyle}
              >
                {HOUR_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {pad2(n)}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-center">
              <div className="small text-muted mb-1">Min</div>
              <select
                aria-label="Minutes"
                value={pickM}
                onChange={(e) => handlePickerChange("m", e.target.value)}
                style={pickerSelectStyle}
              >
                {MIN_SEC_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {pad2(n)}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-center">
              <div className="small text-muted mb-1">Sec</div>
              <select
                aria-label="Seconds"
                value={pickS}
                onChange={(e) => handlePickerChange("s", e.target.value)}
                style={pickerSelectStyle}
              >
                {MIN_SEC_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {pad2(n)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="small text-muted text-center">
            Type digits only — colons are added automatically
          </div>
        </div>
      )}
    </div>
  );
};

export default ElapsedTimeInput;
